"use client";

import { Assistant, PublicAssistant } from "@/generated/types.gen";
import { motion } from "framer-motion";
import { Folder, LucideIcon, Workflow } from "lucide-react";
import Image from "next/image";
import { NoxusIcon } from "../UI/NoxusIcon";
import useAssistantToolDetails from "../Assistant/useAssistantToolDetails";
import { Tooltip, TooltipContent, TooltipTrigger } from "../shadcn/tooltip";

interface NewChatEmptyStateProps {
  assistant: Assistant | PublicAssistant | undefined;
  groupId: string;
  isPublic: boolean;
  agentIconUrl?: string;
  isGenie?: boolean;
}

export const NewChatEmptyState = ({
  assistant,
  isPublic,
  agentIconUrl,
  isGenie,
}: NewChatEmptyStateProps) => {
  const { workflowNames, knowledgeBaseNames } = useAssistantToolDetails({
    definition:
      assistant && "definition" in assistant ? assistant.definition : undefined,
    isPublic,
  });

  const workflowNamesArray = Object.values(workflowNames || {}) as string[];
  const knowledgeBaseNamesArray = Object.values(
    knowledgeBaseNames || {}
  ) as string[];

  const allChips: { icon: LucideIcon; name: string; key: string }[] = [
    ...workflowNamesArray.map((name, i) => ({
      icon: Workflow,
      name,
      key: `wf-${i}`,
    })),
    ...knowledgeBaseNamesArray.map((name, i) => ({
      icon: Folder,
      name,
      key: `kb-${i}`,
    })),
  ];

  const MAX_CHIPS = 4;
  const visibleChips =
    allChips.length > MAX_CHIPS ? allChips.slice(0, MAX_CHIPS - 1) : allChips;
  const hiddenChips =
    allChips.length > MAX_CHIPS ? allChips.slice(MAX_CHIPS - 1) : [];

  const hasTools = allChips.length > 0;

  return (
    <div
      key="empty-state"
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
    >
      <div className="pointer-events-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{
            opacity: 0,
            scale: 0.95,
            filter: "blur(10px)",
            transition: { duration: 0.3 },
          }}
          className="flex flex-col items-center justify-center min-h-[400px] w-full max-w-2xl mx-auto px-6 text-center"
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              delay: 0.2,
              type: "spring",
              stiffness: 200,
              damping: 20,
            }}
            className="relative mb-10 group"
          >
            <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full scale-150 opacity-50 group-hover:opacity-100 transition-opacity duration-500" />
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="relative z-10 shadow-md rounded-[1.25rem] transition-transform duration-500"
            >
              {agentIconUrl ? (
                <div className="relative w-24 h-24 overflow-hidden rounded-[1.25rem]">
                  <Image
                    src={agentIconUrl}
                    alt={assistant?.name || "Assistant"}
                    fill
                    className="object-cover"
                  />
                </div>
              ) : isGenie ? (
                <div className="relative w-16 h-16 flex items-center justify-center bg-white rounded-2xl">
                  <Image
                    src="/logos/noxus-o.svg"
                    alt="Noxus"
                    width={32}
                    height={32}
                    className="w-8 h-8"
                  />
                </div>
              ) : (
                <NoxusIcon
                  icon={assistant?.image_url || "coworker"}
                  size="xxl"
                  isPublic={isPublic}
                />
              )}
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h1 className="text-4xl font-medium mb-4 tracking-tighter line-clamp-2 px-2 leading-tight">
              {assistant?.name || (isGenie ? "Noxus Agent" : "Agent")}
            </h1>
          </motion.div>

          {(assistant?.description || isGenie) && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-sm text-muted-foreground/80 mb-8 leading-normal max-w-lg font-medium line-clamp-3 px-2"
            >
              {assistant?.description ||
                "AI assistant that helps you navigate and build on the Noxus platform."}
            </motion.p>
          )}

          {hasTools && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap gap-4 justify-center"
            >
              {visibleChips.map((chip, i) => (
                <ToolChip
                  key={chip.key}
                  icon={chip.icon}
                  name={chip.name}
                  delay={0.6 + i * 0.1}
                />
              ))}
              {hiddenChips.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.6 + visibleChips.length * 0.1 }}
                      className="flex items-center gap-2 px-3 py-1.25 rounded-full border bg-background/50 backdrop-blur-sm cursor-default"
                    >
                      <span className="text-sm font-medium">
                        {hiddenChips.length} more
                      </span>
                    </motion.div>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <ul className="flex flex-col gap-1">
                      {hiddenChips.map((chip) => (
                        <li key={chip.key} className="flex items-center gap-2">
                          <chip.icon size={12} />
                          <span>{chip.name}</span>
                        </li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

const ToolChip = ({
  icon: Icon,
  name,
  delay,
}: {
  icon: LucideIcon;
  name: string;
  delay: number;
}) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay }}
    className="flex items-center gap-2 px-3 py-1.25 rounded-full border bg-background/50 backdrop-blur-sm"
  >
    <Icon size={14} className="text-primary" />
    <span className="text-sm font-medium">{name}</span>
  </motion.div>
);
